import { useMemo, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useTranslation } from 'react-i18next';
import { foldSearch } from '@eis/core/reporting';
import { FieldMenu, type FieldMenuAction } from './FieldMenu';
import { displayFieldName, typeBadge, type ShelfId } from './fields';

/** One field the report can use (a catalog column, a custom dataset's alias, a calculated field). */
export interface FieldListItem {
  readonly name: string;
  /** The catalog type, or `CALCULATED`. */
  readonly type: string;
  /** The field-list group (R9): `Police`, `Konto`, `car` … */
  readonly group: string;
  /** A title override from the report, if any. */
  readonly label?: string;
}

export interface FieldListProps {
  readonly fields: readonly FieldListItem[];
  /** "Verschieben nach …" from the menu or the shortcut, and a drop on a shelf. */
  readonly onMove: (field: string, shelf: ShelfId) => void;
  readonly onAddCalculated?: () => void;
  /** Further menu actions of one field — "Bearbeiten" and "Entfernen" of a calculated field. */
  readonly fieldActions?: (field: FieldListItem) => readonly FieldMenuAction[];
  /** The MIME type a drag carries the field name in; the shelves accept it. */
  readonly dragType?: string;
}

export const FIELD_DRAG_TYPE = 'application/x-eis-field';

/**
 * The editor's field list (SDD-024 U-1; mockup board 4): the dataset's fields, searchable, grouped
 * by prefix with their type badges. Every field drags onto a shelf (native HTML5 drag, no library)
 * and has the same moves in its menu — Enter or Shift+F10 opens it, and R / C / V / F move the
 * field there at once (U-2): the keyboard path is first-class, not a fallback.
 */
export function FieldList({
  fields,
  onMove,
  onAddCalculated,
  fieldActions,
  dragType = FIELD_DRAG_TYPE,
}: FieldListProps): ReactNode {
  const { t } = useTranslation('patterns');
  const [search, setSearch] = useState('');
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [menu, setMenu] = useState<{ anchor: HTMLElement; field: FieldListItem } | null>(null);

  const visible = useMemo(() => {
    const terms = foldSearch(search.trim());
    if (terms.length === 0) return fields;
    return fields.filter((f) => {
      const text = foldSearch(`${f.name} ${f.label ?? ''} ${f.group}`).join(' ');
      return terms.every((term) => text.includes(term));
    });
  }, [fields, search]);

  const groups = useMemo(() => {
    const map = new Map<string, FieldListItem[]>();
    for (const f of visible) {
      const key = f.type === 'CALCULATED' ? t('reporting.fields.calculated') : f.group;
      map.set(key, [...(map.get(key) ?? []), f]);
    }
    return [...map.entries()];
  }, [visible, t]);

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" component="h2">
          {t('reporting.fields.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('reporting.fields.count', { count: fields.length })}
        </Typography>
      </Stack>
      <TextField
        size="small"
        value={search}
        placeholder={t('reporting.fields.search')}
        slotProps={{ htmlInput: { 'aria-label': t('reporting.fields.search') } }}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
      />
      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('reporting.fields.noMatch')}
        </Typography>
      )}
      <Box
        sx={{ overflowY: 'auto', maxHeight: 560 }}
        tabIndex={0}
        role="group"
        aria-label={t('reporting.fields.title')}
      >
        {groups.map(([group, items]) => {
          const open = search !== '' || !closed.has(group);
          const listId = `field-group-${group.replace(/[^A-Za-z0-9]+/g, '-')}`;
          return (
            <Box key={group} sx={{ mb: 0.5 }}>
              <Button
                size="small"
                color="inherit"
                fullWidth
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => {
                  setClosed((prev) => {
                    const next = new Set(prev);
                    if (next.has(group)) next.delete(group);
                    else next.add(group);
                    return next;
                  });
                }}
                endIcon={
                  open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />
                }
                sx={{ justifyContent: 'space-between', fontWeight: 600, textTransform: 'none' }}
              >
                {group} ({items.length})
              </Button>
              <Collapse in={open}>
                <List dense disablePadding id={listId} aria-label={group}>
                  {items.map((field) => {
                    const shown = field.label ?? displayFieldName(field.name);
                    const badge = typeBadge(field.type);
                    return (
                      <ListItem key={field.name} disablePadding>
                        <ListItemButton
                          draggable
                          aria-haspopup="menu"
                          data-field={field.name}
                          onDragStart={(event) => {
                            const transfer = event.dataTransfer as DataTransfer | null;
                            if (transfer === null) return;
                            transfer.effectAllowed = 'copyMove';
                            transfer.setData(dragType, field.name);
                            transfer.setData('text/plain', field.name);
                          }}
                          onClick={(event) => {
                            setMenu({ anchor: event.currentTarget, field });
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'F10' && event.shiftKey) {
                              event.preventDefault();
                              setMenu({ anchor: event.currentTarget, field });
                            }
                          }}
                          sx={{ py: 0.25, borderRadius: 1 }}
                        >
                          <Chip
                            size="small"
                            label={badge}
                            aria-hidden
                            variant="outlined"
                            sx={{ mr: 1, height: 18, fontSize: '0.6875rem', minWidth: 34 }}
                          />
                          <ListItemText primary={shown} slotProps={{ primary: { noWrap: true } }} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>
      {onAddCalculated !== undefined && (
        <Button
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={onAddCalculated}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('reporting.fields.addCalculated')}
        </Button>
      )}
      <FieldMenu
        anchorEl={menu?.anchor ?? null}
        open={menu !== null}
        label={t('reporting.fields.menu', {
          field: menu === null ? '' : (menu.field.label ?? displayFieldName(menu.field.name)),
        })}
        onClose={() => {
          setMenu(null);
        }}
        moveTargets={['rows', 'columns', 'values', 'filters']}
        onMove={(shelf) => {
          if (menu !== null) onMove(menu.field.name, shelf);
        }}
        display={menu === null || fieldActions === undefined ? [] : fieldActions(menu.field)}
      />
    </Stack>
  );
}
